import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, resource } from '@angular/core';
import { lastValueFrom, map } from 'rxjs';
import { Option } from '../../models/common.models';
import { DeviceCareCenterResponse } from '../../models/device-care-center.model';
import { RegionService } from '../region/region.service';
import { WarehouseService } from '../warehouse/warehouse.service';
import { mapDeviceCareCenterResponseToModel } from './device-care-center.mapper';

@Injectable({
  providedIn: 'root',
})
export class DeviceCareCenterService {
  http = inject(HttpClient);
  warehouseService = inject(WarehouseService);
  regions = inject(RegionService);

  deviceCareCenters$ = (regionId: number) =>
    this.http
      .get<DeviceCareCenterResponse[]>('DeviceCareCenter', {
        params: new HttpParams().set('regionid', String(regionId)),
      })
      .pipe(
        map((response) =>
          response.map((r) => mapDeviceCareCenterResponseToModel(r)),
        ),
      );

  deviceCareCenters = resource({
    params: () => this.regions.selectedRegionId(),
    loader: ({ params }) => lastValueFrom(this.deviceCareCenters$(params)),
    defaultValue: [],
  });

  deviceCareCenterOptions = computed<Option[]>(() => {
    const uniqueDccCodes = this.warehouseService.uniqueDccCodes();

    return this.deviceCareCenters
      .value()
      .filter((dcc) => uniqueDccCodes.includes(dcc.code))
      .map((dcc) => {
        return { label: dcc.name, value: dcc.code };
      });
  });

  downloadDeviceCareCenters() {
    return this.http.get('DeviceCareCenter/download', {
      responseType: 'blob',
      observe: 'response',
    });
  }

  getDeviceCareCenter(code: string) {
    return this.deviceCareCenters.value().find((dcc) => dcc.code === code);
  }

  getDeviceCareCenterById = (code: string) => {
    return this.http
      .get<DeviceCareCenterResponse>('DeviceCareCenter/' + code)
      .pipe(
        map((response: DeviceCareCenterResponse) =>
          mapDeviceCareCenterResponseToModel(response),
        ),
      );
  };
}
